import { useState } from 'react';

import { buttonClass } from '@/components/ui';

/**
 * "Download CSV" for a list screen. Tracker #11.
 *
 * Fetched, not a plain link. A link to a route that refuses (too many lines,
 * signed out) saves the refusal as a file called stock-on-hand.csv, and Guy
 * opens a spreadsheet with one cell of JSON in it. Here the refusal is shown
 * next to the button, in the words the server wrote.
 *
 * `params` are the filters on screen, so the file is the list he is looking
 * at, every page of it.
 */
export function DownloadCsv(
  { path, params, label = 'Download CSV' }:
  { path: string; params: Record<string, string | undefined>; label?: string },
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
      const res = await fetch(`${path}?${search.toString()}`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        setError(body?.error?.message ?? `The download did not work (${res.status}). Try again.`);
        return;
      }
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export.csv';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('The download did not work. Check the connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" className={buttonClass('secondary', 'sm')} onClick={download} disabled={busy}>
        {busy ? 'Preparing...' : label}
      </button>
      {error && <span role="alert" className="text-[13px] text-danger">{error}</span>}
    </span>
  );
}
